import { EMPTY_STR } from "@/constants/string";
import {
  FIRST_DISCOUNT_PRODUCT_NAME,
  OTHER_DIMENSION_PRODUCT_NAME,
  PRICE_DIMENSION,
  REFUND_TYPE,
  TPriceDimension,
} from "@/features/organization/product/constants/productEnums";
import {
  TPriceCombination,
  TProduct,
  TRefundCondition,
  TTypePrice,
} from "@/api/services/main/organization/product/dtos/product.dto";

/**
 * Display formatters + predicates for the 商品設定 screens. Detail<->form mapping lives in
 * productFormMappers.ts (kept separate so this file stays focused on read-side helpers).
 */

/**
 * 単価（税抜）display. Returns EMPTY_STR.TEXT ("-") when the price is not configured
 * (initial state for a 固定商品), otherwise "100,000円 / 件".
 */
export const formatUnitPrice = (unitPrice: number | null, unit: string | null): string => {
  if (unitPrice == null) return EMPTY_STR.TEXT;
  const price = unitPrice.toLocaleString("en-US");
  return unit ? `${price}円 / ${unit}` : `${price}円`;
};

/** Whether a row can be selected (and therefore deleted). 固定商品 can never be selected. */
export const isProductSelectable = (product: TProduct): boolean => !product.isDefault;

/** 小計 = 単価 − 特定技能2号値引き, from raw form strings ("-" upstream when null). */
export const rowSubtotal = (unitPrice: string | null, discount: string | null): number | null => {
  const base = parseUnitPrice(unitPrice);
  if (base == null) return null;
  return base - (parseUnitPrice(discount) ?? 0);
};

/** Parses the form's string price back into a number (null when blank). */
export const parseUnitPrice = (value: string | null): number | null => {
  if (value == null) return null;
  const normalized = value.replace(/,/g, "").trim();
  if (!/^-?\d+$/.test(normalized)) return null;
  return Number(normalized);
};

/** "25,000円" — money display, "-" when null. */
export const formatYen = (value: number | null | undefined): string =>
  value == null ? EMPTY_STR.TEXT : `${value.toLocaleString("en-US")}円`;

/** "25,000円 / 名" — money per unit (defaults to 名), "-" when null. */
export const formatYenPerName = (value: number | null | undefined, unit = "名"): string =>
  value == null ? EMPTY_STR.TEXT : `${value.toLocaleString("en-US")}円 / ${unit}`;

/** "3ヶ月" — months display, "-" when null. */
export const formatMonths = (value: number | null | undefined): string =>
  value == null ? EMPTY_STR.TEXT : `${value}ヶ月`;

/** 返金額（返金率）— "80％返金" / "50,000円返金". */
export const formatRefund = (cond: TRefundCondition): string => {
  if (cond.refundValue == null) return EMPTY_STR.TEXT;
  const unit = cond.refundType === REFUND_TYPE.PERCENT ? "％" : "円";
  const value = cond.refundType === REFUND_TYPE.PERCENT ? cond.refundValue : cond.refundValue.toLocaleString("en-US");
  return `${value}${unit}返金`;
};

/** 初回割引期間 / 単価 are shown only for 支援委託費 (C2). */
export const showsFirstDiscount = (productName: string): boolean => productName === FIRST_DISCOUNT_PRODUCT_NAME;
/** 種別単価 shows その他 instead of ティア for 人材紹介料 (C3). */
export const usesOtherDimension = (productName: string): boolean => productName === OTHER_DIMENSION_PRODUCT_NAME;

/**
 * Values already chosen in sibling rows (excluding `index`) — used to disable duplicate選択 in
 * 国籍 / 分野 (種別単価) and 在職期間 (返金条件). Same dedupe logic, shared here.
 */
export const usedValuesExcept = (values: (string | null | undefined)[], index: number): string[] =>
  values.filter((value, i): value is string => i !== index && value != null && value !== "");

/**
 * 種別単価 dimensions shown for a product: 国籍 / 分野 always; その他 for 人材紹介料 (C3); ティア when
 * the product is a tier target; otherwise just 国籍 / 分野.
 */
export const getTypeDimensions = (productName: string, isTierDiscountTarget: boolean): TPriceDimension[] => {
  if (usesOtherDimension(productName)) return [PRICE_DIMENSION.NATIONALITY, PRICE_DIMENSION.FIELD, PRICE_DIMENSION.OTHER];
  if (isTierDiscountTarget) return [PRICE_DIMENSION.NATIONALITY, PRICE_DIMENSION.FIELD, PRICE_DIMENSION.TIER];
  return [PRICE_DIMENSION.NATIONALITY, PRICE_DIMENSION.FIELD];
};

export type TComboEnabled = { nationality: boolean; field: boolean; tier: boolean };

/**
 * 単価/名 for a 単価組み合わせ row = sum of the matching 種別単価 unit prices across enabled dimensions.
 * For each ON dimension: a picked value uses that value's 単価; a blank (= 全て) value uses the
 * dimension's default-row 単価; anything without a 単価 counts as 0. Returns null when no dimension is
 * ON (nothing to combine → "-").
 */
/**
 * How a 単価組み合わせ cell identifies the 種別単価 row it selected, per dimension. This is the one
 * place the (inherent) asymmetry lives:
 *   - 国籍 / 分野 are master-backed → identified by the master id the row carries (nationalityId / fieldId);
 *   - ティア / その他 have no master (mutable free text) → identified by the row's own id.
 */
const DIMENSION_MATCH_KEY = {
  [PRICE_DIMENSION.NATIONALITY]: "nationalityId",
  [PRICE_DIMENSION.FIELD]: "fieldId",
  [PRICE_DIMENSION.TIER]: "id",
  [PRICE_DIMENSION.OTHER]: "id",
} as const satisfies Record<TPriceDimension, "nationalityId" | "fieldId" | "id">;

type TTypePriceRowLike = {
  dimension: TPriceDimension;
  isDefault: boolean;
  id: string;
  nationalityId: string | number | null;
  fieldId: string | number | null;
};

/**
 * The 種別単価 row a combination cell points at for `dimension`: the row whose match key equals the
 * selection, or the dimension's 全て default row when nothing is selected (blank). Shared by the form
 * and detail price resolvers so the matching rule is defined once.
 */
export const findSelectedTypePriceRow = <T extends TTypePriceRowLike>(
  rows: T[],
  dimension: TPriceDimension,
  selectedKey: string | number | null | undefined,
): T | undefined => {
  if (selectedKey == null || selectedKey === "") {
    return rows.find((row) => row.dimension === dimension && row.isDefault);
  }
  const matchKey = DIMENSION_MATCH_KEY[dimension];
  return rows.find((row) => row.dimension === dimension && String(row[matchKey] ?? "") === String(selectedKey));
};

/** The three combination dimensions in fixed order, with their enabled flag + selected key. */
export const comboDimensions = (
  sel: { nationalityId?: string | number | null; fieldId?: string | number | null; variantTypeId?: string | number | null },
  enabled: TComboEnabled,
  thirdDim: TPriceDimension,
): { dimension: TPriceDimension; on: boolean; key: string | number | null | undefined }[] => [
  { dimension: PRICE_DIMENSION.NATIONALITY, on: enabled.nationality, key: sel.nationalityId },
  { dimension: PRICE_DIMENSION.FIELD, on: enabled.field, key: sel.fieldId },
  { dimension: thirdDim, on: enabled.tier, key: sel.variantTypeId },
];

/**
 * Detail-screen variant of resolveComboPrice (numeric ids). Keeps the same business rules as edit:
 * selected value -> that row price, blank/null -> default row price, missing price -> 0.
 */
export const resolveComboPriceForDetail = (
  typePrices: TTypePrice[],
  row: Pick<TPriceCombination, "nationalityId" | "fieldId" | "variantTypeId">,
  enabled: TComboEnabled,
  thirdDim: TPriceDimension,
): number | null => {
  const active = comboDimensions(row, enabled, thirdDim).filter((d) => d.on);
  if (!active.length) return null;
  return active.reduce((sum, d) => {
    const match = findSelectedTypePriceRow(typePrices, d.dimension, d.key);
    return sum + (match?.unitPrice ?? 0);
  }, 0);
};

/**
 * Display text for a combination's tier/other reference — the referenced 種別単価 row's current
 * `variantValue`, or "-" when unset / dangling. Detail + edit both derive the label this way so a
 * rename shows immediately.
 */
export const getVariantTypeLabel = (
  typePrices: Pick<TTypePrice, "id" | "variantValue">[],
  variantTypeId: number | string | null | undefined,
): string => {
  if (variantTypeId == null) return EMPTY_STR.TEXT;
  const row = typePrices.find((t) => String(t.id) === String(variantTypeId));
  return row?.variantValue || EMPTY_STR.TEXT;
};

/**
 * 国籍 / 分野 / ティア each keep a pinned 全て… default row; その他 does not — it's free-text 項目 rows
 * only (C3), so no default option is shown for it.
 */
export const dimensionHasDefaultRow = (dimension: TPriceDimension): boolean =>
  dimension !== PRICE_DIMENSION.OTHER;

/**
 * Label for the pinned default row (国籍 / 分野 / ティア). その他 has no default row — see
 * dimensionHasDefaultRow — so its entry is unused (kept only to satisfy the Record).
 */
export const DEFAULT_OPTION_LABEL: Record<TPriceDimension, string> = {
  [PRICE_DIMENSION.NATIONALITY]: "全ての国籍",
  [PRICE_DIMENSION.FIELD]: "全ての分野",
  [PRICE_DIMENSION.TIER]: "全てのティア",
  [PRICE_DIMENSION.OTHER]: "",
};

/**
 * Effective condition key of a 単価組み合わせ row — dimensions that are OFF count as 全て ("*"), so two
 * rows that differ only in an OFF dimension are still duplicates. Used to block duplicate conditions.
 */
const comboConditionKey = (
  row: { nationalityId: string | null; fieldId: string | null; variantTypeId: string | null },
  enabled: TComboEnabled,
): string =>
  [
    enabled.nationality ? row.nationalityId ?? "" : "*",
    enabled.field ? row.fieldId ?? "" : "*",
    enabled.tier ? row.variantTypeId ?? "" : "*",
  ].join("|");

/** Whether any 単価組み合わせ row repeats another's condition (mirrors the backend unique constraint). */
export const hasDuplicateCombinations = (
  rows: { nationalityId: string | null; fieldId: string | null; variantTypeId: string | null }[],
  enabled: TComboEnabled,
): boolean => {
  const seen = new Set<string>();
  const duplicates: number[] = [];
  rows.forEach((row, index) => {
    const key = comboConditionKey(row, enabled);
    if (seen.has(key)) duplicates.push(index);
    else seen.add(key);
  });
  return duplicates.length > 0;
};
